import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    Validators
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { first } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { serializeDate } from '../../../core/helpers';
import { ClientField } from '../../../domain/models';
import { SelectionService } from '../../../selection/services';
import { DisclosureForm } from '../../models';

@Component({
    selector: 'app-disclosure-form',
    templateUrl: './disclosure-form.component.html',
    styleUrls: ['./disclosure-form.component.scss'],
    standalone: false
})
export class DisclosureFormComponent {
    disclosure = DisclosureForm.initialize(this._fb);
    isParentSignatureRequired: boolean = false;

    @Input() submitting: boolean = false;

    @Input()
    set fields(value: ClientField[] | null) {
        if (value) DisclosureForm.setFields(value, this.disclosure);
    }

    @Input() // TODO: NO `any`
    set value(v: any) {
        this.disclosure.patchValue({ ...v });

        if (this.disclosure.value?.studentSignatureDate == null)
            this.studentSignatureDate?.setValue(new Date());

        if (this.disclosure.value?.parentSignatureDate == null)
            this.parentSignatureDate?.setValue(new Date());

        if (this.disclosure.value?.isParentSignatureRequired) {
            this.isParentSignatureRequired = true;
            this.parentFirstName?.addValidators([Validators.required]);
            this.parentLastName?.addValidators([Validators.required]);
            this.parentSignatureDate?.addValidators([Validators.required]);
        } else {
            this.isParentSignatureRequired = false;
            this.parentFirstName?.removeValidators(Validators.required);
            this.parentLastName?.removeValidators(Validators.required);
            this.parentSignatureDate?.removeValidators(Validators.required);
            this.parentFirstName?.updateValueAndValidity();
            this.parentLastName?.updateValueAndValidity();
            this.parentSignatureDate?.updateValueAndValidity();
        }
    }

    @Output() submitted = new EventEmitter<any>();

    releaseOfInformationSelections$ =
        this.selectionService.releaseOfInformationSelections$;

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly snackBar: MatSnackBar,
        private readonly _transloco: TranslocoService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService,

        private readonly selectionService: SelectionService
    ) {}

    get touched() {
        return this.disclosure.touched;
    }

    get studentFirstName(): AbstractControl | null {
        return this.disclosure.get('studentFirstName');
    }

    get studentLastName(): AbstractControl | null {
        return this.disclosure.get('studentLastName');
    }

    get studentSignatureDate(): AbstractControl | null {
        return this.disclosure.get('studentSignatureDate');
    }

    get studentHasSigned(): AbstractControl | null {
        return this.disclosure.get('studentHasSigned');
    }

    get parentFirstName(): AbstractControl | null {
        return this.disclosure.get('parentFirstName');
    }

    get parentLastName(): AbstractControl | null {
        return this.disclosure.get('parentLastName');
    }

    get parentSignatureDate(): AbstractControl | null {
        return this.disclosure.get('parentSignatureDate');
    }

    get parentHasSigned(): AbstractControl | null {
        return this.disclosure.get('parentHasSigned');
    }

    get submitIsEnabled(): boolean {
        return (
            this.disclosure.value?.studentHasSigned &&
            (!this.isParentSignatureRequired ||
                this.disclosure.value?.parentHasSigned)
        );
    }

    get releaseOfInformationIds(): AbstractControl | null {
        return this.disclosure.get('releaseOfInformationIds');
    }

    get dataSharingAgreed(): AbstractControl | null {
        return this.disclosure.get('dataSharingAgreed');
    }

    onSubmit(event: any): void {
        this.disclosure.markAllAsTouched();
        if (this.disclosure.valid && this.submitIsEnabled) {
            // Disable the submit button
            this.submitting = true;

            // Convert the studentSignatureDate Date object to a string representation of just the date
            this.disclosure.value.studentSignatureDate = serializeDate(
                this.disclosure.value.studentSignatureDate
            );

            // Convert the studentSignatureDate Date object to a string representation of just the date
            this.disclosure.value.parentSignatureDate = serializeDate(
                this.disclosure.value.parentSignatureDate
            );

            this.submitted.next(this.disclosure.value);
        } else {
            this.submitting = false;
            this._transloco
                .selectTranslate('application.snack.invalid-form')
                .pipe(first())
                .subscribe((message) =>
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    })
                );

            this._googleAnalyticsService.formError(this.disclosure);
        }
    }
}
